/**
 * **NOTE** The component in this file is a direct copy of the styles found at:
 * `src/modules/templateEditor/EditPush/`.
 *
 * Like everything else in this module, this work exists under the assumption that it is temporary.
 * We _should_ be using the templateEditor module's preview for this module, but the current template diff
 * module does not fetch its data from grapqhl, a necessity for the templateEdit preview.
 */

import { useState } from 'react';
import { FlexSpacer } from 'ui/flexSpacer/FlexSpacer';
import { Container } from 'ui/container/Container';
import { RelativeTime } from 'ui/utility/relativeTime';
import { SegmentedControl } from 'ui/segmentedControl';
import { Select } from 'ui/selectv2/Select';
import { ContentRestoreButton } from '../ContentRestoreButton';
import { StyledDL } from '../ContentPreview.styled';
import { VIEW_OPTIONS, SELECT_OPTIONS } from './constants';
import { NotificationIos } from './NotificationIos';
import { NotificationAndroid } from './NotificationAndroid';
import { ExpandedNotificationIos } from './ExpandedNotificationIos';
import { ExpandedNotificationAndroid } from './ExpandedNotificationAndroid';
import type { TemplateDiffContentPreviewProps, TemplateDiffContentPush } from '../../../types';
import { DeviceContainer, PushPreviewContainer } from './ContentPreviewPush.styled';

type ContentPreviewPushProps = TemplateDiffContentPush & TemplateDiffContentPreviewProps;

export const ContentPreviewPush = ({
  onClick,
  templateId,
  ...content
}: ContentPreviewPushProps) => {
  const [previewViewOption, setPreviewViewOption] = useState<'apple' | 'android'>('apple');
  const [selectedPreviewType, setSelectedPreviewType] = useState<(typeof SELECT_OPTIONS)[0]>(
    SELECT_OPTIONS[0],
  );

  return (
    <Container size="medium" variant="primary" shadow="small">
      <FlexSpacer flexDirection="column" alignItems="stretch" gap="actionGap">
        <FlexSpacer justifyContent="space-between" alignItems="baseline">
          <StyledDL>
            <dt>Created</dt>
            <dd>
              <RelativeTime timestamp={content.createdAt} />
            </dd>
            <dt>Created by</dt>
            <dd>{content.creatorUserId}</dd>
            <dt>Title</dt>
            <dd>{content.title}</dd>
            <dt>Message</dt>
            <dd>{content.message}</dd>
          </StyledDL>
          <ContentRestoreButton contentId={content.id} onClick={() => onClick(content)} />
        </FlexSpacer>
        <Container size="small" hasBorder variant="secondary">
          <PushPreviewContainer>
            <DeviceContainer data-device={previewViewOption} data-test="template-diff-push-preview">
              {previewViewOption === 'apple' && selectedPreviewType.value === 'notification' && (
                <NotificationIos {...content} />
              )}
              {previewViewOption === 'apple' &&
                selectedPreviewType.value === 'expandedNotification' && (
                  <ExpandedNotificationIos {...content} />
                )}
              {previewViewOption === 'android' && selectedPreviewType.value === 'notification' && (
                <NotificationAndroid {...content} />
              )}
              {previewViewOption === 'android' &&
                selectedPreviewType.value === 'expandedNotification' && (
                  <ExpandedNotificationAndroid {...content} />
                )}
            </DeviceContainer>
          </PushPreviewContainer>
        </Container>
        <FlexSpacer justifyContent="flex-start">
          <SegmentedControl
            data-test="template-diff-push-segmented-control"
            onSelect={setPreviewViewOption}
            options={VIEW_OPTIONS}
            value={previewViewOption}
          />
          <Select
            usePortal
            value={[selectedPreviewType]}
            id="template-diff-push-preview-type-selector"
            onSelect={([newPreviewType]) => {
              setSelectedPreviewType(newPreviewType as (typeof SELECT_OPTIONS)[0]);
            }}
            options={SELECT_OPTIONS}
            menuPlacement="top"
          />
        </FlexSpacer>
      </FlexSpacer>
    </Container>
  );
};
